import { useEffect, useState } from 'react';
import axios from '../axios';
import { getTopLevelCategories } from '../config/categoryRoutes';

const CACHE_TTL_MS = 5 * 60 * 1000;

let cachedTopCategories = null;
let cachedAt = 0;
let pendingPromise = null;

const fetchTopCategoriesFromApi = async () => {
  const response = await axios.get('/api/categories');
  return getTopLevelCategories(response.data);
};

export const getTopCategoriesCached = async (force = false) => {
  const now = Date.now();
  const isCacheValid =
    cachedTopCategories && now - cachedAt < CACHE_TTL_MS;

  if (!force && isCacheValid) {
    return cachedTopCategories;
  }

  if (!force && pendingPromise) {
    return pendingPromise;
  }

  pendingPromise = fetchTopCategoriesFromApi()
    .then((categories) => {
      cachedTopCategories = categories;
      cachedAt = Date.now();
      return categories;
    })
    .finally(() => {
      pendingPromise = null;
    });

  return pendingPromise;
};

export const clearTopCategoriesCache = () => {
  cachedTopCategories = null;
  cachedAt = 0;
};

export function useTopCategories({ enabled = true } = {}) {
  const [topCategories, setTopCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setTopCategories([]);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    getTopCategoriesCached()
      .then((categories) => {
        if (!isMounted) return;
        setTopCategories(categories);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to fetch top categories:', err);
        if (!isMounted) return;
        setTopCategories([]);
        setError(err);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [enabled]);

  const refetch = async (force = false) => {
    const categories = await getTopCategoriesCached(force);
    setTopCategories(categories);
    return categories;
  };

  return { topCategories, isLoading, error, refetch };
}
